import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import MultiEntityMentionsEditor, {
  ENTITY_CONFIGS,
  EntitySuggestion,
  MultiEntityMentionsEditorRef,
} from '../components/MultiEntityMentionsEditor';
import { getAuthHeaders } from '../utils/auth';
import { API_URL } from '../constants/urls';

interface NarrativeRecord {
  id: string;
  title: string;
  narrative_text: string;
  north_star?: string;
  timeframe_start?: string;
  timeframe_end?: string;
  status: string;
  version?: number;
}

interface LocationState {
  copyFrom?: {
    title: string;
    narrative_text: string;
    north_star?: string;
    timeframe_start?: string;
    timeframe_end?: string;
  };
}

export default function StrategicNarrativeEditPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const organizationId = searchParams.get('organization_id') || '';

  const editorRef = useRef<MultiEntityMentionsEditorRef>(null);
  const [loading, setLoading] = useState<boolean>(!!id);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    narrative_text: '',
    north_star: '',
    timeframe_start: '',
    timeframe_end: '',
    status: 'draft',
  });

  const isReadOnly =
    formData.status === 'active' || formData.status === 'superseded';

  useEffect(() => {
    const fetchNarrative = async () => {
      if (!id || !organizationId) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const response = await axios.get(
          `${API_URL}/strategic-narratives/${id}?organization_id=${organizationId}`,
          { headers: getAuthHeaders() },
        );
        const n: NarrativeRecord = response.data;
        setFormData({
          title: n.title || '',
          narrative_text: n.narrative_text || '',
          north_star: n.north_star || '',
          timeframe_start: n.timeframe_start || '',
          timeframe_end: n.timeframe_end || '',
          status: n.status || 'draft',
        });
      } catch (e) {
        setError('Failed to load narrative');
      } finally {
        setLoading(false);
      }
    };

    // Reset form when navigating to /strategy/new (no id)
    if (!id) {
      // Check if we're copying from an existing version
      const copyFrom = (location.state as LocationState)?.copyFrom;
      if (copyFrom) {
        setFormData({
          title: copyFrom.title || '',
          narrative_text: copyFrom.narrative_text || '',
          north_star: copyFrom.north_star || '',
          timeframe_start: copyFrom.timeframe_start || '',
          timeframe_end: copyFrom.timeframe_end || '',
          status: 'draft', // Always draft for new versions
        });
      } else {
        setFormData({
          title: '',
          narrative_text: '',
          north_star: '',
          timeframe_start: '',
          timeframe_end: '',
          status: 'draft',
        });
      }
      setError(null);
      setLoading(false);
    } else {
      fetchNarrative();
    }
  }, [id, organizationId, location.state]);

  const handleSearchEntities = async (
    entityType: string,
    query: string,
  ): Promise<EntitySuggestion[]> => {
    // Strategic narratives only support: okr, segment, proposition, company
    // User mentions are NOT supported in strategic narratives (by design - narratives link business entities)
    const supportedTypes = ['okr', 'segment', 'proposition', 'company'];

    if (!supportedTypes.includes(entityType)) {
      // Return empty array for unsupported types (like 'user')
      // This prevents 422 errors when the generic @ trigger is activated
      return [];
    }

    try {
      const response = await axios.get(
        `${API_URL}/strategic-narratives/entities/search`,
        {
          params: {
            entity_type: entityType,
            query: query || '',
            limit: 15,
            organization_id: organizationId,
          },
          headers: getAuthHeaders(),
        },
      );
      return response.data;
    } catch (err) {
      console.error(`Error searching ${entityType}:`, err);
      return [];
    }
  };

  const handleSave = async (activate = false) => {
    setSaving(true);
    setError(null);
    try {
      const tokenizedContent =
        editorRef.current?.getTokenizedContent() || formData.narrative_text;

      if (!formData.title.trim()) {
        setError('Title is required');
        setSaving(false);
        return;
      }
      if (!tokenizedContent.trim()) {
        setError('Narrative text is required');
        setSaving(false);
        return;
      }

      const payload: any = {
        title: formData.title.trim(),
        narrative_text: tokenizedContent, // Don't trim - preserve user's whitespace formatting
        status: activate ? 'active' : formData.status,
      };
      if (formData.north_star.trim())
        payload.north_star = formData.north_star.trim();
      if (formData.timeframe_start)
        payload.timeframe_start = formData.timeframe_start;
      if (formData.timeframe_end)
        payload.timeframe_end = formData.timeframe_end;

      if (id) {
        await axios.put(
          `${API_URL}/strategic-narratives/${id}?organization_id=${organizationId}`,
          payload,
          {
            headers: getAuthHeaders(),
          },
        );
      } else {
        await axios.post(
          `${API_URL}/strategic-narratives?organization_id=${organizationId}`,
          payload,
          {
            headers: getAuthHeaders(),
          },
        );
      }

      navigate('/strategy', { state: { refresh: Date.now() } });
    } catch (e: any) {
      const msg =
        e?.response?.data?.detail ||
        e?.response?.data?.message ||
        'Failed to save strategic narrative';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            {id
              ? isReadOnly
                ? 'View Strategic Narrative'
                : 'Edit Strategic Narrative'
              : 'Create Strategic Narrative'}
          </h1>
          {formData.status && id && (
            <p className="text-sm text-gray-500 mt-1">
              Status: {formData.status}
            </p>
          )}
        </div>
        {isReadOnly && id && (
          <button
            type="button"
            onClick={() => {
              // Navigate to new strategy page with current data pre-filled
              navigate(`/strategy/new?organization_id=${organizationId}`, {
                state: {
                  copyFrom: {
                    title: formData.title,
                    narrative_text: formData.narrative_text,
                    north_star: formData.north_star,
                    timeframe_start: formData.timeframe_start,
                    timeframe_end: formData.timeframe_end,
                  },
                },
              });
            }}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            Create New Version
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Title *
          </label>
          <input
            type="text"
            required
            disabled={isReadOnly}
            value={formData.title}
            onChange={(e) =>
              setFormData({ ...formData, title: e.target.value })
            }
            className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm sm:text-sm ${
              isReadOnly
                ? 'bg-gray-100 border-gray-200 cursor-not-allowed'
                : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
            }`}
            placeholder="FY2026 Strategic Growth Plan"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            North Star Mission (Optional)
          </label>
          <textarea
            rows={2}
            disabled={isReadOnly}
            value={formData.north_star}
            onChange={(e) =>
              setFormData({ ...formData, north_star: e.target.value })
            }
            className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm sm:text-sm ${
              isReadOnly
                ? 'bg-gray-100 border-gray-200 cursor-not-allowed'
                : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
            }`}
            placeholder="Be the most helpful AI partner for complex, regulated enterprises..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Strategic Narrative *
          </label>
          <MultiEntityMentionsEditor
            ref={editorRef}
            value={formData.narrative_text}
            onChange={(value) =>
              setFormData({ ...formData, narrative_text: value })
            }
            placeholder="Type @okr, @segment, @proposition, or @company to link..."
            onSearchEntities={handleSearchEntities}
            disabled={isReadOnly}
            entityConfigOverrides={{
              okr: {
                label: 'Corporate OKRs',
                searchPlaceholder: 'Search Corporate OKRs...',
              } as Partial<typeof ENTITY_CONFIGS.okr>,
            }}
          />
          <div className="text-xs text-gray-500 mt-2">
            <span className="font-medium">Tip:</span> Use @okr to link corporate
            OKRs, or @segment, @proposition, @company to link other entities in
            your narrative
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Start Date (Optional)
            </label>
            <input
              type="date"
              disabled={isReadOnly}
              value={formData.timeframe_start || ''}
              onChange={(e) =>
                setFormData({ ...formData, timeframe_start: e.target.value })
              }
              className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm sm:text-sm ${
                isReadOnly
                  ? 'bg-gray-100 border-gray-200 cursor-not-allowed'
                  : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
              }`}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              End Date (Optional)
            </label>
            <input
              type="date"
              disabled={isReadOnly}
              value={formData.timeframe_end || ''}
              onChange={(e) =>
                setFormData({ ...formData, timeframe_end: e.target.value })
              }
              className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm sm:text-sm ${
                isReadOnly
                  ? 'bg-gray-100 border-gray-200 cursor-not-allowed'
                  : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
              }`}
            />
          </div>
        </div>

        <div className="mt-6 pt-4 border-t">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Status
          </label>
          <select
            disabled={isReadOnly}
            value={formData.status}
            onChange={(e) =>
              setFormData({ ...formData, status: e.target.value })
            }
            className={`mt-1 block w-full px-3 py-2 border-2 rounded-md shadow-sm sm:text-sm ${
              isReadOnly
                ? 'bg-gray-100 border-gray-200 cursor-not-allowed'
                : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500 bg-white'
            }`}
            style={{ minHeight: '40px' }}
          >
            <option value="draft">Draft</option>
            <option value="active">Active</option>
          </select>
          {formData.status === 'active' && (
            <p className="mt-1 text-sm text-gray-500">
              Setting to active will deactivate any other active narratives.
            </p>
          )}
        </div>
      </div>

      <div className="mt-8 flex justify-end gap-2 border-t pt-4">
        <button
          type="button"
          onClick={() =>
            navigate('/strategy', { state: { refresh: Date.now() } })
          }
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          Cancel
        </button>
        {!isReadOnly && (
          <>
            <button
              type="button"
              disabled={saving}
              onClick={() => handleSave(false)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              {saving ? 'Saving...' : id ? 'Update' : 'Create'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
